/**
 * Reine Aggregations-/Umrechnungsfunktionen fuer den Dividenden-Tracker (Feedback-Runde
 * 4, Punkt 19, Vorbild Divvydiary) - kein I/O, nur Gruppierung/Berechnung ueber bereits
 * geladene manuelle Dividenden-Eintraege (supabase/migrations/0023_dividend_events.sql).
 */

export interface DividendEvent {
  id: string;
  symbol: string;
  companyName: string;
  amount: number;
  currency: string;
  paymentDate: string;
  /** Runde 8 Punkt 1+27: `false` heisst `amount` ist bereits netto (Nutzer hat das beim
   * Eintragen explizit so angegeben, z.B. weil der Broker die Steuer schon einbehalten
   * hat) - fliesst dann OHNE erneuten Steuerabzug in die Netto-Summen ein, statt wie der
   * Standardfall (`true`/`undefined`, brutto) den Steuerabzug zu simulieren. Optional mit
   * implizitem Default "brutto" fuer Aufrufer/Tests, die das Feld nicht setzen. */
  isGross?: boolean;
}

function isAlreadyNet(e: DividendEvent): boolean {
  return e.isGross === false;
}

/** Grobe deutsche Kapitalertragsteuer-Näherung (Abgeltungsteuer 25 % + Solidaritätszuschlag
 * 5,5 % darauf = 26,375 % effektiv) - bewusst OHNE Sparerpauschbetrag/Kirchensteuer, da
 * beide individuell und Erikonomie keine persönliche Steuersituation kennt. Klar als
 * Näherung ausgewiesen, keine Steuerberatung. */
export const GERMAN_WITHHOLDING_TAX_RATE = 0.26375;

/** Jaehrlicher Sparerpauschbetrag pro Person (Stand 2023, seither unveraendert) - siehe
 * computeNetAmountWithAllowance. Eigene Konstante statt hartkodiert, damit eine
 * kuenftige Gesetzesaenderung an einer Stelle nachziehbar ist. */
export const GERMAN_ANNUAL_ALLOWANCE_EUR = 1000;

export function computeNetAmount(grossAmount: number): number {
  return grossAmount * (1 - GERMAN_WITHHOLDING_TAX_RATE);
}

/**
 * Wie computeNetAmount, beruecksichtigt aber zusaetzlich den vom Nutzer selbst
 * angegebenen, bereits anderweitig (ausserhalb dieser App) verbrauchten Anteil des
 * Sparerpauschbetrags (Nutzer-Feedback 2026-07-18) - der REST des Freibetrags mindert
 * den steuerpflichtigen Anteil, bevor die 26,375 % auf den ueberschiessenden Betrag
 * angewendet werden. `allowanceUsedElsewhere` negativ oder > `allowanceLimit` wird auf
 * den gueltigen Bereich [0, allowanceLimit] begrenzt, statt eine negative Steuer/einen
 * negativen Restfreibetrag entstehen zu lassen. `allowanceLimit` optional mit Default
 * `GERMAN_ANNUAL_ALLOWANCE_EUR` (Einzelperson) - fuer den "Gemeinschaftskonto"-
 * Umschalter (Runde 8 Punkt 3+28) mit 2x GERMAN_ANNUAL_ALLOWANCE_EUR aufrufbar. Weiterhin
 * nur eine Naeherung (kein Sparerpauschbetrag fuer Kapitalertraege ausserhalb dieser App
 * automatisch bekannt, keine Kirchensteuer) - keine Steuerberatung.
 */
export function computeNetAmountWithAllowance(
  grossAmount: number,
  allowanceUsedElsewhere: number,
  allowanceLimit: number = GERMAN_ANNUAL_ALLOWANCE_EUR,
): number {
  const usedElsewhere = Math.min(allowanceLimit, Math.max(0, allowanceUsedElsewhere));
  const remainingAllowance = allowanceLimit - usedElsewhere;
  const taxableAmount = Math.max(0, grossAmount - remainingAllowance);
  const tax = taxableAmount * GERMAN_WITHHOLDING_TAX_RATE;
  return grossAmount - tax;
}

export interface GroupedTotal {
  key: string;
  label: string;
  grossTotal: number;
  netTotal: number;
}

/** Effektiver Netto-Anteil (0..1) fuer einen ganzen Jahres-Bruttobetrag - mit oder ohne
 * Freibetrags-Beruecksichtigung. Auf JEDEN Bucket (Quartal/Monat) desselben Jahres
 * gleichmaessig angewendet, statt eine willkuerliche Reihenfolge "diese Dividende ist
 * steuerfrei, jene nicht" zu unterstellen - Summe der Bucket-Nettobetraege ergibt so
 * exakt den Jahres-Nettobetrag. Exportiert, da aggregateActualVsProjectedByMonth
 * unten denselben Satz auch auf die Prognose-Seite anwendet. */
export function effectiveNetRate(
  yearGross: number,
  allowanceUsedElsewhere: number | undefined,
  allowanceLimit: number = GERMAN_ANNUAL_ALLOWANCE_EUR,
): number {
  if (yearGross <= 0) return 1;
  const net =
    allowanceUsedElsewhere === undefined
      ? computeNetAmount(yearGross)
      : computeNetAmountWithAllowance(yearGross, allowanceUsedElsewhere, allowanceLimit);
  return net / yearGross;
}

/** Nur Ereignisse derselben Waehrung sollten hier zusammen aggregiert werden (Aufrufer
 * filtert vorher nach Waehrung) - Aufsummieren ueber Waehrungen hinweg ohne Umrechnung
 * waere irrefuehrend, gleiche Begruendung wie die Waehrungs-Trennung im Depot-Roentgenblick.
 * `allowance` (Nutzer-Feedback 2026-07-18) wirkt NUR auf das darin angegebene Jahr - der
 * Sparerpauschbetrag gilt pro Kalenderjahr, ein heute eingegebener "bereits verbraucht"-
 * Wert liesse sich nicht sinnvoll auf vergangene Jahre uebertragen. */
export function aggregateByYear(
  events: DividendEvent[],
  allowance?: { year: number; usedElsewhere: number },
  allowanceLimit: number = GERMAN_ANNUAL_ALLOWANCE_EUR,
): GroupedTotal[] {
  const grossByYear = new Map<number, number>();
  const alreadyNetByYear = new Map<number, number>();
  for (const e of events) {
    const year = new Date(e.paymentDate).getUTCFullYear();
    const map = isAlreadyNet(e) ? alreadyNetByYear : grossByYear;
    map.set(year, (map.get(year) ?? 0) + e.amount);
  }
  const years = new Set([...grossByYear.keys(), ...alreadyNetByYear.keys()]);
  return [...years]
    .sort((a, b) => a - b)
    .map((year) => {
      const gross = grossByYear.get(year) ?? 0;
      const alreadyNet = alreadyNetByYear.get(year) ?? 0;
      // Der Sparerpauschbetrag-Rest gilt nur fuer den Bruttoanteil - bereits netto
      // eingetragene Dividenden hatten ihre eigene Steuerbehandlung schon (z.B. beim
      // Broker), unabhaengig von dem hier getrackten Freibetrags-Rest.
      const netFromGross =
        gross *
        effectiveNetRate(
          gross,
          allowance && year === allowance.year ? allowance.usedElsewhere : undefined,
          allowanceLimit,
        );
      return {
        key: String(year),
        label: String(year),
        // grossTotal fuer bereits-netto-Eintraege nur eine grobe Hochrechnung mit dem
        // pauschalen Steuersatz (fuer die reine Anzeige) - die massgebliche, korrekte
        // Zahl ist netTotal, siehe DividendEvent.isGross-Kommentar.
        grossTotal: gross + alreadyNet / (1 - GERMAN_WITHHOLDING_TAX_RATE),
        netTotal: netFromGross + alreadyNet,
      };
    });
}

const MONTH_LABELS = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

/** Eine einzelne Position innerhalb eines Monats-Balkens (Runde 7 Punkt 6/7 - Hover
 * zeigt, welche Firma wie viel beisteuert, statt nur der Monatssumme). */
export interface DividendLineItem {
  symbol: string;
  name: string;
  logoUrl?: string;
  /** Bereits netto (gleicher Jahres-Satz wie der umschliessende Monats-Balken). */
  amountNet: number;
  /** Nur bei erhaltenen Ereignissen gesetzt. */
  date?: string;
  /** Nur bei Prognose-Eintraegen gesetzt - true nur fuer das von Yahoo bestaetigte
   * naechste Ex-Datum je Aktie, sonst aus dem Zahlungsrhythmus geschaetzt. */
  confirmed?: boolean;
}

export interface MonthlyActualVsProjected {
  /** 0-11. */
  monthIndex: number;
  label: string;
  /** Netto, bereits erhalten (manuell erfasste dividend_events dieses Monats). */
  actualNet: number;
  /** Netto, noch erwartet (Prognose aus den Depot-Positionen, siehe load-projection.ts) -
   * 0 fuer bereits vergangene Monate, da loadDividendProjection nur ab heute vorausschaut. */
  projectedNet: number;
  actualItems: DividendLineItem[];
  projectedItems: DividendLineItem[];
}

export interface ProjectedEntryForAggregation {
  symbol: string;
  name: string;
  logoUrl?: string;
  monthIndex: number;
  amount: number;
  confirmed: boolean;
}

/**
 * Kombiniert bereits erhaltene (Dividenden-Tracker) und noch erwartete (Depot-Prognose)
 * Dividenden desselben Kalenderjahrs zu einer gemeinsamen Monatsreihe (Erik-Auftrag
 * 2026-07-18: "wie viel ... dieses Jahr bisher eingetroffen sind und wie viele ... noch
 * kommen werden (netto)"; erweitert Runde 7 Punkt 6/7 um die einzelnen Positionen je
 * Monat fuer die Hover-Aufschluesselung + um einen frei waehlbaren `year` statt nur des
 * laufenden Kalenderjahrs). Wie aggregateByMonth wird EIN effektiver Netto-Satz fuer das
 * GESAMTE Jahr (erhalten + erwartet zusammen) ermittelt und gleichmaessig auf jeden
 * Monats-Bucket UND jede einzelne Position darin angewendet, statt eine willkuerliche
 * Reihenfolge zu unterstellen - genau dieselbe Begruendung wie bei den bereits
 * bestehenden Aggregationen oben. Beide Eingaben muessen bereits auf dieselbe Waehrung
 * gefiltert sein (Aufrufer gruppiert nach Waehrung), sonst waere die Summe irrefuehrend -
 * gleiche Konvention wie aggregateByYear/-Quarter/-Month. */
export function aggregateActualVsProjectedByMonth(
  actualEvents: DividendEvent[],
  projectedEntries: ProjectedEntryForAggregation[],
  year: number,
  allowanceUsedElsewhere?: number,
): MonthlyActualVsProjected[] {
  const actualByMonth = new Map<number, DividendEvent[]>();
  for (const e of actualEvents) {
    const date = new Date(e.paymentDate);
    if (date.getUTCFullYear() !== year) continue;
    const month = date.getUTCMonth();
    const list = actualByMonth.get(month) ?? [];
    list.push(e);
    actualByMonth.set(month, list);
  }

  const projectedByMonth = new Map<number, ProjectedEntryForAggregation[]>();
  for (const p of projectedEntries) {
    const entryYear = Math.floor(p.monthIndex / 12);
    if (entryYear !== year) continue;
    const month = p.monthIndex % 12;
    const list = projectedByMonth.get(month) ?? [];
    list.push(p);
    projectedByMonth.set(month, list);
  }

  const sum = (list: { amount: number }[]) => list.reduce((total, item) => total + item.amount, 0);
  // Bereits-netto eingetragene Ereignisse (isGross === false) fliessen NICHT in den
  // steuerpflichtigen Pool ein, dem der Sparerpauschbetrag-Rest zugutekommt - siehe
  // gleiche Begruendung in aggregateByYear.
  const actualGrossOnly = (list: DividendEvent[]) => list.filter((e) => !isAlreadyNet(e));
  const actualAlreadyNetOnly = (list: DividendEvent[]) => list.filter(isAlreadyNet);
  const actualYearGross = [...actualByMonth.values()].reduce(
    (total, list) => total + sum(actualGrossOnly(list)),
    0,
  );
  const projectedYearGross = [...projectedByMonth.values()].reduce((total, list) => total + sum(list), 0);
  const rate = effectiveNetRate(actualYearGross + projectedYearGross, allowanceUsedElsewhere);

  return Array.from({ length: 12 }, (_, month) => {
    const actual = actualByMonth.get(month) ?? [];
    const projected = projectedByMonth.get(month) ?? [];
    return {
      monthIndex: month,
      label: MONTH_LABELS[month],
      actualNet: sum(actualGrossOnly(actual)) * rate + sum(actualAlreadyNetOnly(actual)),
      projectedNet: sum(projected) * rate,
      actualItems: actual.map((e) => ({
        symbol: e.symbol,
        name: e.companyName,
        amountNet: isAlreadyNet(e) ? e.amount : e.amount * rate,
        date: e.paymentDate,
      })),
      projectedItems: projected.map((p) => ({
        symbol: p.symbol,
        name: p.name,
        logoUrl: p.logoUrl,
        amountNet: p.amount * rate,
        confirmed: p.confirmed,
      })),
    };
  });
}

export interface YearlyHistoryForecastBar {
  year: number;
  /** Netto, tatsaechlich erhalten (0 fuer reine Zukunftsjahre). */
  actualNet: number;
  /** Netto, prognostiziert (0 fuer reine Vergangenheitsjahre; fuers laufende Jahr der
   * noch ausstehende Rest, fuer Zukunftsjahre der komplette Jahresbetrag). */
  forecastNet: number;
  /** Pro-Position-Aufschlüsselung des `actualNet`-Anteils (Feedback-Runde 13 Punkt 3,
   * 2026-07-22 - gleiches Prinzip wie MonthlyActualVsProjected.actualItems). Leer, wenn
   * `actualEvents` nicht übergeben wurde. */
  actualItems: DividendLineItem[];
  /** Analog für `forecastNet`. Leer, wenn `projectedEntries` nicht übergeben wurde. */
  forecastItems: DividendLineItem[];
}

type SymbolTotal = { name: string; logoUrl?: string; total: number };

/** Addiert einen Betrag in die verschachtelte Jahr->Symbol-Summenkarte (Hilfsfunktion für
 * die Pro-Position-Zeilen unten, kein eigenständig exportierter Aggregations-Baustein). */
function addToYearSymbolTotals(
  byYear: Map<number, Map<string, SymbolTotal>>,
  year: number,
  symbol: string,
  name: string,
  logoUrl: string | undefined,
  amount: number,
): void {
  const bySymbol = byYear.get(year) ?? new Map<string, SymbolTotal>();
  const existing = bySymbol.get(symbol);
  bySymbol.set(symbol, { name, logoUrl: logoUrl ?? existing?.logoUrl, total: (existing?.total ?? 0) + amount });
  byYear.set(year, bySymbol);
}

/** Verteilt einen bereits feststehenden Jahres-Nettobetrag (`targetNet`) verhältnismäßig
 * auf die Roh-Beträge je Symbol (Feedback-Runde 13 Punkt 3) - dieselbe "ein Satz aufs
 * ganze Jahr, gleichmäßig auf jeden Bucket"-Logik wie in aggregateActualVsProjectedByMonth,
 * hier nur nach dem bereits berechneten Zielbetrag zurückgerechnet statt den Satz selbst
 * neu herzuleiten. Garantiert per Konstruktion: Summe der Zeilen === targetNet, unabhängig
 * davon, ob targetNet aus der einfachen Flat-Rate (Vergangenheit) oder der
 * Sparerpauschbetrag-bewussten Berechnung (laufendes Jahr) stammt. */
function distributeProportionally(
  bySymbol: Map<string, SymbolTotal>,
  targetNet: number,
): DividendLineItem[] {
  const grossSum = [...bySymbol.values()].reduce((sum, v) => sum + v.total, 0);
  if (grossSum <= 0) return [];
  const rate = targetNet / grossSum;
  return [...bySymbol.entries()]
    .map(([symbol, v]) => ({ symbol, name: v.name, logoUrl: v.logoUrl, amountNet: v.total * rate }))
    .sort((a, b) => b.amountNet - a.amountNet);
}

/**
 * Fuehrt die tatsaechliche Jahres-Historie (aggregateByYear) mit der Depot-basierten
 * Mehrjahres-Prognose (projectMonthlyDividendIncome/-Reinvested) zu EINER Balkenreihe
 * zusammen (Runde 8 Punkt 3+28, "EIN Chart mit Historie + Prognose statt zwei
 * getrennter Charts"). Das laufende Jahr bekommt bewusst BEIDE Anteile (bereits
 * erhalten + fuer die restlichen Monate noch erwartet, aus
 * aggregateActualVsProjectedByMonth() aufsummiert) statt nur einem der beiden - sonst
 * wuerde der Balken fuers laufende Jahr je nach Datenquelle unvollstaendig wirken.
 * `futureForecastPointsGross` nur mit Jahren > currentYear befuellen (Jahr <=
 * currentYear wird hier ignoriert, die Aufteilung fuer das laufende Jahr kommt
 * ausschliesslich aus den expliziten `currentYear*`-Parametern). Reine Funktion, kein I/O.
 *
 * `actualEvents`/`projectedEntries` (Feedback-Runde 13 Punkt 3, 2026-07-22, beide optional
 * mit Default `[]` fuer Abwaertskompatibilitaet zu bestehenden Aufrufen/Tests) liefern die
 * Pro-Position-Aufschlüsselung je Balken (`actualItems`/`forecastItems`) fuer den
 * Jahresansicht-Tooltip, analog zur bereits bestehenden Monatsansicht.
 */
export function buildYearlyHistoryForecastBars(
  historicalYears: { year: number; netTotal: number }[],
  currentYear: number,
  currentYearActualNet: number,
  currentYearForecastNet: number,
  futureForecastPointsGross: { year: number; grossAnnual: number }[],
  allowanceLimit: number = GERMAN_ANNUAL_ALLOWANCE_EUR,
  actualEvents: DividendEvent[] = [],
  projectedEntries: ProjectedEntryForAggregation[] = [],
): YearlyHistoryForecastBar[] {
  const actualBySymbolByYear = new Map<number, Map<string, SymbolTotal>>();
  for (const e of actualEvents) {
    const year = new Date(e.paymentDate).getUTCFullYear();
    addToYearSymbolTotals(actualBySymbolByYear, year, e.symbol, e.companyName, undefined, e.amount);
  }
  const projectedBySymbolByYear = new Map<number, Map<string, SymbolTotal>>();
  for (const p of projectedEntries) {
    const year = Math.floor(p.monthIndex / 12);
    addToYearSymbolTotals(projectedBySymbolByYear, year, p.symbol, p.name, p.logoUrl, p.amount);
  }

  const bars: YearlyHistoryForecastBar[] = historicalYears
    .filter((h) => h.year < currentYear)
    .map((h) => ({
      year: h.year,
      actualNet: h.netTotal,
      forecastNet: 0,
      actualItems: distributeProportionally(actualBySymbolByYear.get(h.year) ?? new Map(), h.netTotal),
      forecastItems: [],
    }));

  bars.push({
    year: currentYear,
    actualNet: currentYearActualNet,
    forecastNet: currentYearForecastNet,
    actualItems: distributeProportionally(actualBySymbolByYear.get(currentYear) ?? new Map(), currentYearActualNet),
    forecastItems: distributeProportionally(projectedBySymbolByYear.get(currentYear) ?? new Map(), currentYearForecastNet),
  });

  for (const p of futureForecastPointsGross) {
    if (p.year <= currentYear) continue;
    // Jedes Zukunftsjahr bekommt einen frischen Sparerpauschbetrag (kein "bereits
    // verbraucht"-Anteil bekannt) - gleiches Prinzip wie die bisherige
    // DividendIncomeProjection (enrichPoints()).
    const forecastNet = computeNetAmountWithAllowance(p.grossAnnual, 0, allowanceLimit);
    bars.push({
      year: p.year,
      actualNet: 0,
      forecastNet,
      actualItems: [],
      forecastItems: distributeProportionally(projectedBySymbolByYear.get(p.year) ?? new Map(), forecastNet),
    });
  }

  return bars.sort((a, b) => a.year - b.year);
}

export interface PlayfulRates {
  perYear: number;
  perMonth: number;
  perDay: number;
  perHour: number;
}

const DAYS_PER_YEAR = 365.25;

/** "Spielerische" Aufschluesselung eines Jahresbetrags auf kleinere Zeiteinheiten
 * (Feedback-Runde 4, Punkt 19: "wie viel Nettodividende man pro Monat/Tag/Stunde
 * erhält") - reine Division, keine eigene Berechnung/Prognose. */
export function computePlayfulRates(trailingYearTotal: number): PlayfulRates {
  return {
    perYear: trailingYearTotal,
    perMonth: trailingYearTotal / 12,
    perDay: trailingYearTotal / DAYS_PER_YEAR,
    perHour: trailingYearTotal / (DAYS_PER_YEAR * 24),
  };
}

export interface TrailingYearSum {
  /** Brutto-Anteil (isGross !== false) - unterliegt beim Aufrufer noch dem Steuerabzug. */
  grossPortion: number;
  /** Bereits-netto eingetragener Anteil (isGross === false) - fliesst beim Aufrufer OHNE
   * erneuten Steuerabzug ein, siehe DividendEvent.isGross-Kommentar. */
  alreadyNetPortion: number;
}

/** Summe der letzten 365 Tage ab `asOf`, aufgeteilt in Brutto-/Bereits-netto-Anteil
 * (Runde 8 Punkt 1+27) - Basis fuer die spielerischen Pro-Zeiteinheit-Kennzahlen
 * (stabiler als das laufende Kalenderjahr allein, das z.B. im Januar kaum
 * Datenpunkte haette). */
export function sumTrailingYear(events: DividendEvent[], asOf: Date): TrailingYearSum {
  const cutoff = new Date(asOf.getTime() - DAYS_PER_YEAR * 24 * 60 * 60 * 1000);
  const inWindow = events.filter((e) => {
    const d = new Date(e.paymentDate);
    return d >= cutoff && d <= asOf;
  });
  return {
    grossPortion: inWindow.filter((e) => !isAlreadyNet(e)).reduce((sum, e) => sum + e.amount, 0),
    alreadyNetPortion: inWindow.filter(isAlreadyNet).reduce((sum, e) => sum + e.amount, 0),
  };
}
